import { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Send as SendIcon,
    PlayArrow as ProcessIcon,
    SmartToy as SmartToyIcon
} from '@mui/icons-material';
import CustomButton from 'src/components/atoms/buttons/CustomButton';

// Toggle for visual debugging
const VISUAL_DEBUG = false;

export const AiAutocompleteTest = ({ addHighlightToField }) => {
    const { values, setFieldValue } = useFormikContext();

    // When form values change, update the prompt.
    useEffect(() => {
        handlePreprocess();
    }, [values]);

    const [inputPrompt, setInputPrompt] = useState('');
    const [aiResponse, setAiResponse] = useState('');
    const [postProcessed, setPostProcessed] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    const steps = ['Preprocess', 'AI Response', 'Postprocess'];
    const systemInstructions = SYSTEM_INSTRUCTIONS; // assuming this constant is defined elsewhere

    const handlePreprocess = () => {
        const baseInstructions = 'Here is my current form data: ';
        const formikSummary = JSON.stringify(values, null, 2);
        const prompt = `${baseInstructions}\n\n${formikSummary}`;
        setInputPrompt(prompt);
        setActiveStep(1);
    };

    const handleSendToAI = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_SECRET_KEY}` // ⚠️ for testing only
                },
                body: JSON.stringify({
                    model: 'gpt-4o-mini',
                    messages: [
                        { role: 'system', content: systemInstructions },
                        { role: 'user', content: inputPrompt }
                    ],
                    temperature: 0.7
                })
            });

            const data = await response.json();
            const aiContent = data.choices?.[0]?.message?.content || 'No response';
            setAiResponse(aiContent);
            setActiveStep(2);

            // Automatically trigger postprocessing when AI response comes back.
            handlePostprocess(aiContent);
        } catch (err) {
            console.error(err);
            setAiResponse('Error calling OpenAI API.');
        } finally {
            setLoading(false);
        }
    };

    const handlePostprocess = (responseText = aiResponse) => {
        setLoading(true);
        setTimeout(() => {
            try {
                const maybeJson = JSON.parse(responseText);
                if (maybeJson && typeof maybeJson === 'object') {
                    // Update Formik only if the current value is null/empty or an empty object.
                    Object.entries(maybeJson).forEach(([key, value]) => {
                        const formikValue = values[key];
                        const isEmptyObjectWithEmptyValue =
                            typeof formikValue === 'object' &&
                            formikValue !== null &&
                            Object.values(formikValue).every(v => v === '');
                        if (formikValue === null || formikValue === '' || isEmptyObjectWithEmptyValue) {
                            setFieldValue(key, value);
                            addHighlightToField(key);
                        }
                    });
                    const stripped = {
                        status: maybeJson.status || 'unknown',
                        summary: maybeJson.summary || JSON.stringify(maybeJson)
                    };
                    setPostProcessed(JSON.stringify(stripped, null, 2));
                } else {
                    setPostProcessed('AI output was not valid JSON');
                }
            } catch (err) {
                console.error(err);
                setPostProcessed('AI output could not be parsed as JSON');
            } finally {
                setActiveStep(3);
                setLoading(false);
            }
        }, 500);
    };

    // Minimal UI when VISUAL_DEBUG is false
    if (!VISUAL_DEBUG) {
        return (
            <CustomButton disabled={loading} iconButton={true} color="secondary" onClick={handleSendToAI} title="" icon={<SmartToyIcon />} />
        );
    }

    // Full UI for visual debugging.
    return (
        <Box sx={{ width: '100%', p: 0 }}>
            <Paper
                elevation={3}
                sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%', boxSizing: 'border-box' }}
            >
                <Typography variant="h5" gutterBottom>
                    AI Content Generator
                </Typography>

                <Stepper
                    activeStep={activeStep}
                    sx={{
                        mb: 4,
                        overflowX: 'auto',
                        '& .MuiStepLabel-root': { whiteSpace: 'nowrap' }
                    }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 }
                        }}
                    >
                        <Typography variant="subtitle1">1. Process Form Data</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePreprocess}
                            startIcon={<ProcessIcon />}
                            disabled={loading}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            Preprocess
                        </Button>
                    </Box>
                    <TextField
                        label="Preprocessed Prompt"
                        multiline
                        rows={5}
                        fullWidth
                        variant="outlined"
                        value={inputPrompt}
                        onChange={(e) => setInputPrompt(e.target.value)}
                        placeholder="Preprocessed prompt will appear here..."
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 }
                        }}
                    >
                        <Typography variant="subtitle1">2. Send to AI Model</Typography>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSendToAI}
                            startIcon={
                                loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />
                            }
                            disabled={!inputPrompt || loading}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            {loading ? 'Sending...' : 'Send to AI'}
                        </Button>
                    </Box>
                    <TextField
                        label="AI Response"
                        multiline
                        rows={5}
                        fullWidth
                        variant="outlined"
                        value={aiResponse}
                        onChange={(e) => setAiResponse(e.target.value)}
                        placeholder="AI response will appear here..."
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 }
                        }}
                    >
                        <Typography variant="subtitle1">3. Process AI Output</Typography>
                        {/* The Postprocess button has been removed since postprocessing now triggers automatically */}
                    </Box>
                    <TextField
                        label="Processed Output"
                        multiline
                        rows={5}
                        fullWidth
                        variant="outlined"
                        value={postProcessed}
                        onChange={(e) => setPostProcessed(e.target.value)}
                        placeholder="Postprocessed output will appear here..."
                    />
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

const PRODUCT_REFERENCE_DATA = {
    // "ProductLines": [
    //     {
    //         "Key": 1,
    //         "Value": "Adidas Originals"
    //     },
    //     {
    //         "Key": 2,
    //         "Value": "Lego"
    //     },
    //     {
    //         "Key": 3,
    //         "Value": "Star Wars Lego"
    //     },
    //     {
    //         "Key": 4,
    //         "Value": "Nike Originals"
    //     }
    // ],
    "ProductStatuses": [
        {
            "Key": 1,
            "Value": "Aktiv"
        },
        {
            "Key": 2,
            "Value": "Inaktiv"
        },
        {
            "Key": 3,
            "Value": "Utgången"
        }
    ],
    "ProductClasses": [
        {
            "Key": 1,
            "Value": "Service",
            "ParentId": null
        },
        {
            "Key": 2,
            "Value": "Lagerartiklar",
            "ParentId": null
        },
        {
            "Key": 3,
            "Value": "Intellectual Property",
            "ParentId": null
        },
        {
            "Key": 4,
            "Value": "Beställningsvara",
            "ParentId": null
        }
    ],
    "ProductSubClasses": [
        {
            "Key": 1,
            "Value": "Software License",
            "ParentId": 3
        },
        {
            "Key": 2,
            "Value": "Insurance",
            "ParentId": 1
        },
        {
            "Key": 3,
            "Value": "Subscription",
            "ParentId": 1
        },
        {
            "Key": 4,
            "Value": "Labor",
            "ParentId": 1
        },
        {
            "Key": 5,
            "Value": "Metervara",
            "ParentId": 2
        },
        {
            "Key": 6,
            "Value": "Styckevara",
            "ParentId": 2
        },
        {
            "Key": 7,
            "Value": "Beställningsvara",
            "ParentId": 4
        },
        {
            "Key": 8,
            "Value": "Tillverkas på beställning",
            "ParentId": 4
        }
    ],
    "ProductTypes": [
        {
            "Key": 7,
            "Value": "CD spelare",
            "ParentId": 1
        },
        {
            "Key": 1,
            "Value": "Elektronik",
            "ParentId": 0
        },
        {
            "Key": 2,
            "Value": "Kläder",
            "ParentId": 0
        },
        {
            "Key": 5,
            "Value": "Lego",
            "ParentId": 4
        },
        {
            "Key": 4,
            "Value": "Leksaker",
            "ParentId": 0
        },
        {
            "Key": 6,
            "Value": "Receivers",
            "ParentId": 1
        },
        {
            "Key": 0,
            "Value": "root",
            "ParentId": null
        },
        {
            "Key": 3,
            "Value": "Skor",
            "ParentId": 2
        }
    ],
    "Manufacturers": [
        {
            "Key": 2,
            "Value": "Adidas"
        },
        {
            "Key": 1,
            "Value": "Default Manufacturer"
        },
        {
            "Key": 3,
            "Value": "Lego"
        },
        {
            "Key": 6,
            "Value": "NAD"
        },
        {
            "Key": 5,
            "Value": "Nike"
        }
    ],
    // "SourcingAssortments": [
    //     {
    //         "Key": 1200000,
    //         "Value": "Default Sourcing Assortment"
    //     }
    // ],
    // "Units": [
    //     {
    //         "Key": 1,
    //         "Value": "m"
    //     },
    //     {
    //         "Key": 2,
    //         "Value": "dm"
    //     },
    //     {
    //         "Key": 3,
    //         "Value": "mm"
    //     },
    //     {
    //         "Key": 4,
    //         "Value": "cm"
    //     },
    //     {
    //         "Key": 5,
    //         "Value": "µm"
    //     },
    //     {
    //         "Key": 6,
    //         "Value": "nm"
    //     },
    //     {
    //         "Key": 7,
    //         "Value": "pm"
    //     },
    //     {
    //         "Key": 8,
    //         "Value": "\""
    //     },
    //     {
    //         "Key": 9,
    //         "Value": "fot"
    //     },
    //     {
    //         "Key": 10,
    //         "Value": "kg"
    //     },
    //     {
    //         "Key": 11,
    //         "Value": "hg"
    //     },
    //     {
    //         "Key": 12,
    //         "Value": "g"
    //     },
    //     {
    //         "Key": 13,
    //         "Value": "mg"
    //     },
    //     {
    //         "Key": 14,
    //         "Value": "oz"
    //     },
    //     {
    //         "Key": 15,
    //         "Value": "lb"
    //     },
    //     {
    //         "Key": 16,
    //         "Value": "oz t"
    //     }
    // ],
    // "MandatoryAssortments": [
    //     {
    //         "Key": 1400000,
    //         "Value": "Default Sales Assortment"
    //     },
    //     {
    //         "Key": 1300000,
    //         "Value": "Default Organisation Assortment"
    //     },
    //     {
    //         "Key": 1100000,
    //         "Value": "Default Supply Assortment"
    //     },
    //     {
    //         "Key": 1200000,
    //         "Value": "Default Sourcing Assortment"
    //     }
    // ],
    // "CountriesOfOrigin": [
    //     {
    //         "Key": 3,
    //         "Value": "AFGHANISTAN"
    //     },
    //     {
    //         "Key": 6,
    //         "Value": "ALBANIA"
    //     },
    //     {
    //         "Key": 61,
    //         "Value": "ALGERIA"
    //     },
    //     {
    //         "Key": 12,
    //         "Value": "AMERICAN SAMOA"
    //     },
    //     {
    //         "Key": 1,
    //         "Value": "ANDORRA"
    //     },
    //     {
    //         "Key": 9,
    //         "Value": "ANGOLA"
    //     },
    //     {
    //         "Key": 5,
    //         "Value": "ANGUILLA"
    //     },
    //     {
    //         "Key": 10,
    //         "Value": "ANTARCTICA"
    //     },
    //     {
    //         "Key": 4,
    //         "Value": "ANTIGUA AND BARBUDA"
    //     },
    //     {
    //         "Key": 11,
    //         "Value": "ARGENTINA"
    //     },
    //     {
    //         "Key": 7,
    //         "Value": "ARMENIA"
    //     },
    //     {
    //         "Key": 15,
    //         "Value": "ARUBA"
    //     },
    //     {
    //         "Key": 14,
    //         "Value": "AUSTRALIA"
    //     },
    //     {
    //         "Key": 13,
    //         "Value": "AUSTRIA"
    //     },
    //     {
    //         "Key": 17,
    //         "Value": "AZERBAIJAN"
    //     },
    //     {
    //         "Key": 32,
    //         "Value": "BAHAMAS"
    //     },
    //     {
    //         "Key": 24,
    //         "Value": "BAHRAIN"
    //     },
    //     {
    //         "Key": 20,
    //         "Value": "BANGLADESH"
    //     },
    //     {
    //         "Key": 19,
    //         "Value": "BARBADOS"
    //     },
    //     {
    //         "Key": 36,
    //         "Value": "BELARUS"
    //     },
    //     {
    //         "Key": 21,
    //         "Value": "BELGIUM"
    //     },
    //     {
    //         "Key": 37,
    //         "Value": "BELIZE"
    //     },
    //     {
    //         "Key": 26,
    //         "Value": "BENIN"
    //     },
    //     {
    //         "Key": 28,
    //         "Value": "BERMUDA"
    //     },
    //     {
    //         "Key": 33,
    //         "Value": "BHUTAN"
    //     },
    //     {
    //         "Key": 30,
    //         "Value": "BOLIVIA, PLURINATIONAL STATE OF"
    //     },
    //     {
    //         "Key": 18,
    //         "Value": "BOSNIA AND HERZEGOVINA"
    //     },
    //     {
    //         "Key": 35,
    //         "Value": "BOTSWANA"
    //     },
    //     {
    //         "Key": 34,
    //         "Value": "BOUVET ISLAND"
    //     },
    //     {
    //         "Key": 31,
    //         "Value": "BRAZIL"
    //     },
    //     {
    //         "Key": 105,
    //         "Value": "BRITISH INDIAN OCEAN TERRITORY"
    //     },
    //     {
    //         "Key": 29,
    //         "Value": "BRUNEI DARUSSALAM"
    //     },
    //     {
    //         "Key": 23,
    //         "Value": "BULGARIA"
    //     },
    //     {
    //         "Key": 22,
    //         "Value": "BURKINA FASO"
    //     },
    //     {
    //         "Key": 25,
    //         "Value": "BURUNDI"
    //     },
    //     {
    //         "Key": 116,
    //         "Value": "CAMBODIA"
    //     },
    //     {
    //         "Key": 47,
    //         "Value": "CAMEROON"
    //     },
    //     {
    //         "Key": 38,
    //         "Value": "CANADA"
    //     },
    //     {
    //         "Key": 52,
    //         "Value": "CAPE VERDE"
    //     },
    //     {
    //         "Key": 123,
    //         "Value": "CAYMAN ISLANDS"
    //     },
    //     {
    //         "Key": 41,
    //         "Value": "CENTRAL AFRICAN REPUBLIC"
    //     },
    //     {
    //         "Key": 212,
    //         "Value": "CHAD"
    //     },
    //     {
    //         "Key": 46,
    //         "Value": "CHILE"
    //     },
    //     {
    //         "Key": 48,
    //         "Value": "CHINA"
    //     },
    //     {
    //         "Key": 53,
    //         "Value": "CHRISTMAS ISLAND"
    //     },
    //     {
    //         "Key": 39,
    //         "Value": "COCOS (KEELING) ISLANDS"
    //     },
    //     {
    //         "Key": 49,
    //         "Value": "COLOMBIA"
    //     },
    //     {
    //         "Key": 118,
    //         "Value": "COMOROS"
    //     },
    //     {
    //         "Key": 42,
    //         "Value": "CONGO"
    //     },
    //     {
    //         "Key": 40,
    //         "Value": "CONGO, THE DEMOCRATIC REPUBLIC OF THE"
    //     },
    //     {
    //         "Key": 45,
    //         "Value": "COOK ISLANDS"
    //     },
    //     {
    //         "Key": 50,
    //         "Value": "COSTA RICA"
    //     },
    //     {
    //         "Key": 44,
    //         "Value": "CÔTE D'IVOIRE"
    //     },
    //     {
    //         "Key": 97,
    //         "Value": "CROATIA"
    //     },
    //     {
    //         "Key": 51,
    //         "Value": "CUBA"
    //     },
    //     {
    //         "Key": 54,
    //         "Value": "CYPRUS"
    //     },
    //     {
    //         "Key": 55,
    //         "Value": "CZECH REPUBLIC"
    //     },
    //     {
    //         "Key": 58,
    //         "Value": "DENMARK"
    //     },
    //     {
    //         "Key": 57,
    //         "Value": "DJIBOUTI"
    //     },
    //     {
    //         "Key": 59,
    //         "Value": "DOMINICA"
    //     },
    //     {
    //         "Key": 60,
    //         "Value": "DOMINICAN REPUBLIC"
    //     },
    //     {
    //         "Key": 62,
    //         "Value": "ECUADOR"
    //     },
    //     {
    //         "Key": 64,
    //         "Value": "EGYPT"
    //     },
    //     {
    //         "Key": 208,
    //         "Value": "EL SALVADOR"
    //     },
    //     {
    //         "Key": 87,
    //         "Value": "EQUATORIAL GUINEA"
    //     },
    //     {
    //         "Key": 66,
    //         "Value": "ERITREA"
    //     },
    //     {
    //         "Key": 63,
    //         "Value": "ESTONIA"
    //     },
    //     {
    //         "Key": 68,
    //         "Value": "ETHIOPIA"
    //     },
    //     {
    //         "Key": 71,
    //         "Value": "FALKLAND ISLANDS (MALVINAS)"
    //     },
    //     {
    //         "Key": 73,
    //         "Value": "FAROE ISLANDS"
    //     },
    //     {
    //         "Key": 70,
    //         "Value": "FIJI"
    //     },
    //     {
    //         "Key": 69,
    //         "Value": "FINLAND"
    //     },
    //     {
    //         "Key": 74,
    //         "Value": "FRANCE"
    //     },
    //     {
    //         "Key": 79,
    //         "Value": "FRENCH GUIANA"
    //     },
    //     {
    //         "Key": 174,
    //         "Value": "FRENCH POLYNESIA"
    //     },
    //     {
    //         "Key": 213,
    //         "Value": "FRENCH SOUTHERN TERRITORIES"
    //     },
    //     {
    //         "Key": 75,
    //         "Value": "GABON"
    //     },
    //     {
    //         "Key": 84,
    //         "Value": "GAMBIA"
    //     },
    //     {
    //         "Key": 78,
    //         "Value": "GEORGIA"
    //     },
    //     {
    //         "Key": 56,
    //         "Value": "GERMANY"
    //     },
    //     {
    //         "Key": 81,
    //         "Value": "GHANA"
    //     },
    //     {
    //         "Key": 82,
    //         "Value": "GIBRALTAR"
    //     },
    //     {
    //         "Key": 88,
    //         "Value": "GREECE"
    //     },
    //     {
    //         "Key": 83,
    //         "Value": "GREENLAND"
    //     },
    //     {
    //         "Key": 77,
    //         "Value": "GRENADA"
    //     },
    //     {
    //         "Key": 86,
    //         "Value": "GUADELOUPE"
    //     },
    //     {
    //         "Key": 91,
    //         "Value": "GUAM"
    //     },
    //     {
    //         "Key": 90,
    //         "Value": "GUATEMALA"
    //     },
    //     {
    //         "Key": 80,
    //         "Value": "GUERNSEY"
    //     },
    //     {
    //         "Key": 85,
    //         "Value": "GUINEA"
    //     },
    //     {
    //         "Key": 92,
    //         "Value": "GUINEA-BISSAU"
    //     },
    //     {
    //         "Key": 93,
    //         "Value": "GUYANA"
    //     },
    //     {
    //         "Key": 98,
    //         "Value": "HAITI"
    //     },
    //     {
    //         "Key": 95,
    //         "Value": "HEARD ISLAND AND MCDONALD ISLANDS"
    //     },
    //     {
    //         "Key": 233,
    //         "Value": "HOLY SEE (VATICAN CITY STATE)"
    //     },
    //     {
    //         "Key": 96,
    //         "Value": "HONDURAS"
    //     },
    //     {
    //         "Key": 94,
    //         "Value": "HONG KONG"
    //     },
    //     {
    //         "Key": 99,
    //         "Value": "HUNGARY"
    //     },
    //     {
    //         "Key": 108,
    //         "Value": "ICELAND"
    //     },
    //     {
    //         "Key": 104,
    //         "Value": "INDIA"
    //     },
    //     {
    //         "Key": 100,
    //         "Value": "INDONESIA"
    //     },
    //     {
    //         "Key": 107,
    //         "Value": "IRAN, ISLAMIC REPUBLIC OF"
    //     },
    //     {
    //         "Key": 106,
    //         "Value": "IRAQ"
    //     },
    //     {
    //         "Key": 101,
    //         "Value": "IRELAND"
    //     },
    //     {
    //         "Key": 103,
    //         "Value": "ISLE OF MAN"
    //     },
    //     {
    //         "Key": 102,
    //         "Value": "ISRAEL"
    //     },
    //     {
    //         "Key": 109,
    //         "Value": "ITALY"
    //     },
    //     {
    //         "Key": 111,
    //         "Value": "JAMAICA"
    //     },
    //     {
    //         "Key": 113,
    //         "Value": "JAPAN"
    //     },
    //     {
    //         "Key": 110,
    //         "Value": "JERSEY"
    //     },
    //     {
    //         "Key": 112,
    //         "Value": "JORDAN"
    //     },
    //     {
    //         "Key": 124,
    //         "Value": "KAZAKHSTAN"
    //     },
    //     {
    //         "Key": 114,
    //         "Value": "KENYA"
    //     },
    //     {
    //         "Key": 117,
    //         "Value": "KIRIBATI"
    //     },
    //     {
    //         "Key": 120,
    //         "Value": "KOREA, DEMOCRATIC PEOPLE'S REPUBLIC OF"
    //     },
    //     {
    //         "Key": 121,
    //         "Value": "KOREA, REPUBLIC OF"
    //     },
    //     {
    //         "Key": 122,
    //         "Value": "KUWAIT"
    //     },
    //     {
    //         "Key": 115,
    //         "Value": "KYRGYZSTAN"
    //     },
    //     {
    //         "Key": 125,
    //         "Value": "LAO PEOPLE'S DEMOCRATIC REPUBLIC"
    //     },
    //     {
    //         "Key": 134,
    //         "Value": "LATVIA"
    //     },
    //     {
    //         "Key": 126,
    //         "Value": "LEBANON"
    //     },
    //     {
    //         "Key": 131,
    //         "Value": "LESOTHO"
    //     },
    //     {
    //         "Key": 130,
    //         "Value": "LIBERIA"
    //     },
    //     {
    //         "Key": 135,
    //         "Value": "LIBYAN ARAB JAMAHIRIYA"
    //     },
    //     {
    //         "Key": 128,
    //         "Value": "LIECHTENSTEIN"
    //     },
    //     {
    //         "Key": 132,
    //         "Value": "LITHUANIA"
    //     },
    //     {
    //         "Key": 133,
    //         "Value": "LUXEMBOURG"
    //     },
    //     {
    //         "Key": 147,
    //         "Value": "MACAO"
    //     },
    //     {
    //         "Key": 143,
    //         "Value": "MACEDONIA, THE FORMER YUGOSLAV REPUBLIC OF"
    //     },
    //     {
    //         "Key": 141,
    //         "Value": "MADAGASCAR"
    //     },
    //     {
    //         "Key": 155,
    //         "Value": "MALAWI"
    //     },
    //     {
    //         "Key": 157,
    //         "Value": "MALAYSIA"
    //     },
    //     {
    //         "Key": 154,
    //         "Value": "MALDIVES"
    //     },
    //     {
    //         "Key": 144,
    //         "Value": "MALI"
    //     },
    //     {
    //         "Key": 152,
    //         "Value": "MALTA"
    //     },
    //     {
    //         "Key": 142,
    //         "Value": "MARSHALL ISLANDS"
    //     },
    //     {
    //         "Key": 149,
    //         "Value": "MARTINIQUE"
    //     },
    //     {
    //         "Key": 150,
    //         "Value": "MAURITANIA"
    //     },
    //     {
    //         "Key": 153,
    //         "Value": "MAURITIUS"
    //     },
    //     {
    //         "Key": 243,
    //         "Value": "MAYOTTE"
    //     },
    //     {
    //         "Key": 156,
    //         "Value": "MEXICO"
    //     },
    //     {
    //         "Key": 72,
    //         "Value": "MICRONESIA, FEDERATED STATES OF"
    //     },
    //     {
    //         "Key": 138,
    //         "Value": "MOLDOVA, REPUBLIC OF"
    //     },
    //     {
    //         "Key": 137,
    //         "Value": "MONACO"
    //     },
    //     {
    //         "Key": 146,
    //         "Value": "MONGOLIA"
    //     },
    //     {
    //         "Key": 139,
    //         "Value": "MONTENEGRO"
    //     },
    //     {
    //         "Key": 151,
    //         "Value": "MONTSERRAT"
    //     },
    //     {
    //         "Key": 136,
    //         "Value": "MOROCCO"
    //     },
    //     {
    //         "Key": 158,
    //         "Value": "MOZAMBIQUE"
    //     },
    //     {
    //         "Key": 145,
    //         "Value": "MYANMAR"
    //     },
    //     {
    //         "Key": 159,
    //         "Value": "NAMIBIA"
    //     },
    //     {
    //         "Key": 168,
    //         "Value": "NAURU"
    //     },
    //     {
    //         "Key": 167,
    //         "Value": "NEPAL"
    //     },
    //     {
    //         "Key": 165,
    //         "Value": "NETHERLANDS"
    //     },
    //     {
    //         "Key": 8,
    //         "Value": "NETHERLANDS ANTILLES"
    //     },
    //     {
    //         "Key": 160,
    //         "Value": "NEW CALEDONIA"
    //     },
    //     {
    //         "Key": 170,
    //         "Value": "NEW ZEALAND"
    //     },
    //     {
    //         "Key": 164,
    //         "Value": "NICARAGUA"
    //     },
    //     {
    //         "Key": 161,
    //         "Value": "NIGER"
    //     },
    //     {
    //         "Key": 163,
    //         "Value": "NIGERIA"
    //     },
    //     {
    //         "Key": 169,
    //         "Value": "NIUE"
    //     },
    //     {
    //         "Key": 162,
    //         "Value": "NORFOLK ISLAND"
    //     },
    //     {
    //         "Key": 148,
    //         "Value": "NORTHERN MARIANA ISLANDS"
    //     },
    //     {
    //         "Key": 166,
    //         "Value": "NORWAY"
    //     },
    //     {
    //         "Key": 171,
    //         "Value": "OMAN"
    //     },
    //     {
    //         "Key": 177,
    //         "Value": "PAKISTAN"
    //     },
    //     {
    //         "Key": 184,
    //         "Value": "PALAU"
    //     },
    //     {
    //         "Key": 182,
    //         "Value": "PALESTINIAN TERRITORY, OCCUPIED"
    //     },
    //     {
    //         "Key": 172,
    //         "Value": "PANAMA"
    //     },
    //     {
    //         "Key": 175,
    //         "Value": "PAPUA NEW GUINEA"
    //     },
    //     {
    //         "Key": 185,
    //         "Value": "PARAGUAY"
    //     },
    //     {
    //         "Key": 173,
    //         "Value": "PERU"
    //     },
    //     {
    //         "Key": 176,
    //         "Value": "PHILIPPINES"
    //     },
    //     {
    //         "Key": 180,
    //         "Value": "PITCAIRN"
    //     },
    //     {
    //         "Key": 178,
    //         "Value": "POLAND"
    //     },
    //     {
    //         "Key": 183,
    //         "Value": "PORTUGAL"
    //     },
    //     {
    //         "Key": 181,
    //         "Value": "PUERTO RICO"
    //     },
    //     {
    //         "Key": 186,
    //         "Value": "QATAR"
    //     },
    //     {
    //         "Key": 187,
    //         "Value": "RÉUNION"
    //     },
    //     {
    //         "Key": 188,
    //         "Value": "ROMANIA"
    //     },
    //     {
    //         "Key": 190,
    //         "Value": "RUSSIAN FEDERATION"
    //     },
    //     {
    //         "Key": 191,
    //         "Value": "RWANDA"
    //     },
    //     {
    //         "Key": 27,
    //         "Value": "SAINT BARTHÉLEMY"
    //     },
    //     {
    //         "Key": 198,
    //         "Value": "SAINT HELENA"
    //     },
    //     {
    //         "Key": 119,
    //         "Value": "SAINT KITTS AND NEVIS"
    //     },
    //     {
    //         "Key": 127,
    //         "Value": "SAINT LUCIA"
    //     },
    //     {
    //         "Key": 140,
    //         "Value": "SAINT MARTIN"
    //     },
    //     {
    //         "Key": 179,
    //         "Value": "SAINT PIERRE AND MIQUELON"
    //     },
    //     {
    //         "Key": 234,
    //         "Value": "SAINT VINCENT AND THE GRENADINES"
    //     },
    //     {
    //         "Key": 241,
    //         "Value": "SAMOA"
    //     },
    //     {
    //         "Key": 203,
    //         "Value": "SAN MARINO"
    //     },
    //     {
    //         "Key": 207,
    //         "Value": "SAO TOME AND PRINCIPE"
    //     },
    //     {
    //         "Key": 192,
    //         "Value": "SAUDI ARABIA"
    //     },
    //     {
    //         "Key": 204,
    //         "Value": "SENEGAL"
    //     },
    //     {
    //         "Key": 189,
    //         "Value": "SERBIA"
    //     },
    //     {
    //         "Key": 194,
    //         "Value": "SEYCHELLES"
    //     },
    //     {
    //         "Key": 202,
    //         "Value": "SIERRA LEONE"
    //     },
    //     {
    //         "Key": 197,
    //         "Value": "SINGAPORE"
    //     },
    //     {
    //         "Key": 201,
    //         "Value": "SLOVAKIA"
    //     },
    //     {
    //         "Key": 199,
    //         "Value": "SLOVENIA"
    //     },
    //     {
    //         "Key": 193,
    //         "Value": "SOLOMON ISLANDS"
    //     },
    //     {
    //         "Key": 205,
    //         "Value": "SOMALIA"
    //     },
    //     {
    //         "Key": 244,
    //         "Value": "SOUTH AFRICA"
    //     },
    //     {
    //         "Key": 89,
    //         "Value": "SOUTH GEORGIA AND THE SOUTH SANDWICH ISLANDS"
    //     },
    //     {
    //         "Key": 67,
    //         "Value": "SPAIN"
    //     },
    //     {
    //         "Key": 129,
    //         "Value": "SRI LANKA"
    //     },
    //     {
    //         "Key": 195,
    //         "Value": "SUDAN"
    //     },
    //     {
    //         "Key": 206,
    //         "Value": "SURINAME"
    //     },
    //     {
    //         "Key": 200,
    //         "Value": "SVALBARD AND JAN MAYEN"
    //     },
    //     {
    //         "Key": 210,
    //         "Value": "SWAZILAND"
    //     },
    //     {
    //         "Key": 196,
    //         "Value": "SWEDEN"
    //     },
    //     {
    //         "Key": 43,
    //         "Value": "SWITZERLAND"
    //     },
    //     {
    //         "Key": 209,
    //         "Value": "SYRIAN ARAB REPUBLIC"
    //     },
    //     {
    //         "Key": 225,
    //         "Value": "TAIWAN, PROVINCE OF CHINA"
    //     },
    //     {
    //         "Key": 216,
    //         "Value": "TAJIKISTAN"
    //     },
    //     {
    //         "Key": 226,
    //         "Value": "TANZANIA, UNITED REPUBLIC OF"
    //     },
    //     {
    //         "Key": 215,
    //         "Value": "THAILAND"
    //     },
    //     {
    //         "Key": 218,
    //         "Value": "TIMOR-LESTE"
    //     },
    //     {
    //         "Key": 214,
    //         "Value": "TOGO"
    //     },
    //     {
    //         "Key": 217,
    //         "Value": "TOKELAU"
    //     },
    //     {
    //         "Key": 221,
    //         "Value": "TONGA"
    //     },
    //     {
    //         "Key": 223,
    //         "Value": "TRINIDAD AND TOBAGO"
    //     },
    //     {
    //         "Key": 220,
    //         "Value": "TUNISIA"
    //     },
    //     {
    //         "Key": 222,
    //         "Value": "TURKEY"
    //     },
    //     {
    //         "Key": 219,
    //         "Value": "TURKMENISTAN"
    //     },
    //     {
    //         "Key": 211,
    //         "Value": "TURKS AND CAICOS ISLANDS"
    //     },
    //     {
    //         "Key": 224,
    //         "Value": "TUVALU"
    //     },
    //     {
    //         "Key": 228,
    //         "Value": "UGANDA"
    //     },
    //     {
    //         "Key": 227,
    //         "Value": "UKRAINE"
    //     },
    //     {
    //         "Key": 2,
    //         "Value": "UNITED ARAB EMIRATES"
    //     },
    //     {
    //         "Key": 76,
    //         "Value": "UNITED KINGDOM"
    //     },
    //     {
    //         "Key": 230,
    //         "Value": "UNITED STATES"
    //     },
    //     {
    //         "Key": 229,
    //         "Value": "UNITED STATES MINOR OUTLYING ISLANDS"
    //     },
    //     {
    //         "Key": 231,
    //         "Value": "URUGUAY"
    //     },
    //     {
    //         "Key": 232,
    //         "Value": "UZBEKISTAN"
    //     },
    //     {
    //         "Key": 240,
    //         "Value": "WALLIS AND FUTUNA"
    //     },
    //     {
    //         "Key": 239,
    //         "Value": "VANUATU"
    //     },
    //     {
    //         "Key": 235,
    //         "Value": "VENEZUELA, BOLIVARIAN REPUBLIC OF"
    //     },
    //     {
    //         "Key": 65,
    //         "Value": "WESTERN SAHARA"
    //     },
    //     {
    //         "Key": 238,
    //         "Value": "VIETNAM"
    //     },
    //     {
    //         "Key": 236,
    //         "Value": "VIRGIN ISLANDS, BRITISH"
    //     },
    //     {
    //         "Key": 237,
    //         "Value": "VIRGIN ISLANDS, U.S."
    //     },
    //     {
    //         "Key": 242,
    //         "Value": "YEMEN"
    //     },
    //     {
    //         "Key": 245,
    //         "Value": "ZAMBIA"
    //     },
    //     {
    //         "Key": 246,
    //         "Value": "ZIMBABWE"
    //     },
    //     {
    //         "Key": 16,
    //         "Value": "ÅLAND ISLANDS"
    //     }
    // ],
    //"PreferredDimensionUnitId": 3,
    //"PreferredWeightUnitId": 12
}

const SYSTEM_INSTRUCTIONS = `You are a background Assistant. You will get data and should follow the format requested. 
    YOU SHOULD ONLY OUTPUT THE REQUESTED JSON, NOTHING ELSE!!. Speed is important so get to it!!
    
    The system is an ERP. 
    Your JSON output will be used to autocomple forms for the user. Based on you best guess and always using the data 
    add data in the field where you feel like you can see where the form-filling is going.
    
    You are presented with 3 sets of information and its very important that you use all of them!

    First - The relevant data about what goes into these fields. For example for Product Type, you get a 
    list with names and Ids of all product types and its these ID's that should be added to the JSON.

    Second. The JSON Format that you should output with the fields and info about the values that goes in them. 
    These are the only ones you should output and only when not yet filled by the user.

    And Last from the user - current state of the form. Your job is to find the required fields in that list 
    and based on the data already added by the user as well as the other data you get, output a JSON where these required fields are filled.

    REFERENCE DATA, THIS IS WHERE YOU FIND THE ID's TO USE IN THE JSON:

    ${JSON.stringify(PRODUCT_REFERENCE_DATA)}

    //OUTPUT JSON FORMAT - This is what you should output!! Put best guess, always use the data you get.
    {
        "ProductType": PRODUCT_TYPE_ID,
        ProductClass: PRODUCT_CLASS_ID, //Almost always "Lagerartiklar" - id 2
        ProductSubClass: PRODUCT_SUBCLASS_ID, //Almost always "Styckevara" - id 6
        "Manufacturer": MANUFACTURER_ID,
    }

    `;